import { createContext, ReactNode } from 'react';
import { PaletteMode } from '@mui/material';

import {
  AppStorageKey,
  DEFAULT_ROWS_PER_PAGE,
  LayoutType,
  MarketPeriodsType
} from 'utils/constants';
import useLocalStorage from 'hooks/useLocalStorage';
import { ConfigProps, CustomizationProps } from 'models/appConfig';
import { UnitType } from 'utils/characteristic';
import { DeviceOrder } from 'views/modules/Dispatcher/HxDashboard/HxPlanning/HxPlanningContent/HxPlanningContentDevice/hxPlanningContentDeviceUtils';
import { Translation } from 'models/i18n';

const initialConfig: ConfigProps = {
  layout: LayoutType.VERTICAL_LAYOUT,
  drawerType: LayoutType.DEFAULT_DRAWER,
  drawerOpen: true,
  fontFamily: `'Roboto', sans-serif`,
  borderRadius: 8,
  outlinedFilled: true,
  navType: 'light' as PaletteMode,
  presetColor: 'default',
  locale: '' as Translation, // locale from keycloak
  container: false,
  productionPlanPeriodDuration: MarketPeriodsType.MARKET_HOURS,
  productionPlanUnits: UnitType.MWH,
  productionPlanPrediction: false,
  localStorageRowsPerPage: DEFAULT_ROWS_PER_PAGE,
  hxDeviceOrder: []
};

const initialState: CustomizationProps = {
  ...initialConfig,
  onChangeLayout: () => {
    // do nothing in initial state
  },
  onChangeDrawer: () => {
    // do nothing in initial state
  },
  onChangeDrawerOpen: () => {
    // do nothing in initial state
  },
  onChangeMenuType: () => {
    // do nothing in initial state
  },
  onChangePresetColor: () => {
    // do nothing in initial state
  },
  onChangeLocale: () => {
    // do nothing in initial state
  },
  onChangeContainer: () => {
    // do nothing in initial state
  },
  onChangeFontFamily: () => {
    // do nothing in initial state
  },
  onChangeBorderRadius: () => {
    // do nothing in initial state
  },
  onChangeOutlinedField: () => {
    // do nothing in initial state
  },
  onChangeProductionPlanPeriodDuration: () => {
    // do nothing in initial state
  },
  onChangeProductionPlanUnits: () => {
    // do nothing in initial state
  },
  onChangeProductionPlanPrediction: () => {
    // do nothing in initial state
  },
  onChangeLocaleStorageRowsPerPage: () => {
    // do nothing in initial state
  },
  onChangeHxDeviceOrder: () => {
    // do nothing in initial state
  }
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState);

type ConfigProviderProps = {
  children: ReactNode;
};

const ConfigProvider = ({ children }: ConfigProviderProps) => {
  const [config, setConfig] = useLocalStorage(
    AppStorageKey.LOCAL_STORAGE_CONFIG,
    {
      layout: initialState.layout,
      drawerType: initialState.drawerType,
      drawerOpen: initialState.drawerOpen,
      fontFamily: initialState.fontFamily,
      borderRadius: initialState.borderRadius,
      outlinedFilled: initialState.outlinedFilled,
      navType: initialState.navType,
      presetColor: initialState.presetColor,
      locale: initialState.locale,
      productionPlanPeriodDuration: initialState.productionPlanPeriodDuration,
      productionPlanUnits: initialState.productionPlanUnits,
      productionPlanPrediction: initialState.productionPlanPrediction,
      localStorageRowsPerPage: initialState.localStorageRowsPerPage,
      hxDeviceOrder: initialState.hxDeviceOrder
    }
  );

  const onChangeLayout = (layout: string) => {
    setConfig({
      ...config,
      layout
    });
  };

  const onChangeDrawer = (drawerType: string) => {
    setConfig({
      ...config,
      drawerType
    });
  };

  const onChangeDrawerOpen = (drawerOpen: boolean) => {
    setConfig({
      ...config,
      drawerOpen
    });
  };

  const onChangeMenuType = (navType: PaletteMode) => {
    setConfig({
      ...config,
      navType
    });
  };

  const onChangePresetColor = (presetColor: string) => {
    setConfig({
      ...config,
      presetColor
    });
  };

  const onChangeLocale = (locale: string) => {
    setConfig({
      ...config,
      locale
    });
  };

  const onChangeContainer = () => {
    setConfig({
      ...config,
      container: !config.container
    });
  };

  const onChangeFontFamily = (fontFamily: string) => {
    setConfig({
      ...config,
      fontFamily
    });
  };

  const onChangeBorderRadius = (_event: Event, newValue: number | number[]) => {
    setConfig({
      ...config,
      borderRadius: newValue as number
    });
  };

  const onChangeOutlinedField = (outlinedFilled: boolean) => {
    setConfig({
      ...config,
      outlinedFilled
    });
  };

  const onChangeProductionPlanPeriodDuration = (
    productionPlanPeriodDuration: MarketPeriodsType
  ): void => {
    setConfig({
      ...config,
      productionPlanPeriodDuration
    });
  };

  const onChangeProductionPlanUnits = (productionPlanUnits: UnitType): void => {
    setConfig({
      ...config,
      productionPlanUnits
    });
  };

  const onChangeProductionPlanPrediction = (
    productionPlanPrediction: boolean
  ): void => {
    setConfig({
      ...config,
      productionPlanPrediction
    });
  };

  const onChangeLocaleStorageRowsPerPage = (
    localStorageRowsPerPage: number
  ): void => {
    setConfig({
      ...config,
      localStorageRowsPerPage
    });
  };

  const onChangeHxDeviceOrder = (hxDeviceOrder: DeviceOrder[]) => {
    setConfig({
      ...config,
      hxDeviceOrder
    });
  };

  return (
    <ConfigContext.Provider
      value={{
        ...config,
        onChangeLayout,
        onChangeDrawer,
        onChangeDrawerOpen,
        onChangeMenuType,
        onChangePresetColor,
        onChangeLocale,
        onChangeContainer,
        onChangeFontFamily,
        onChangeBorderRadius,
        onChangeOutlinedField,
        onChangeProductionPlanPeriodDuration,
        onChangeProductionPlanUnits,
        onChangeProductionPlanPrediction,
        onChangeLocaleStorageRowsPerPage,
        onChangeHxDeviceOrder
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

export { ConfigProvider, ConfigContext };
